import { useTranslation } from "react-i18next";
import { Tag } from "@carbon/react";
import { formatDate } from "../../utils";
import { OfferResponse } from "../../types";
import FileReader from "../FileReader/FileReader";
import OfferPriceEditor from "./OfferPriceEditor";
import "./offerDisplay.scss";

/**
 * The buyer's read view of a seller's offer on a single (non-joint) order (Task 7) — replaces the
 * pre-Carbon Tailwind `dl`/`dt`/`dd` card with the shared `.offer-detail-row` layout, and the
 * inline price edit with `OfferPriceEditor`. Rendered by `components/Offer/Offer.tsx` (unchanged
 * `{ offer: OfferResponse }` prop) from `BuyerBid.tsx`/`JointBuyerBid.tsx`'s side panel.
 */
const IsBuyerOffer = ({ offer }: { offer: OfferResponse }) => {
  const { t } = useTranslation();

  return (
    <div className="offer-display">
      {offer?.selected && (
        <div className="offer-display__success">
          <Tag type="green" size="md">
            {t("offer.success")}
          </Tag>
        </div>
      )}
      <div className="offer-display__card">
        <p className="offer-display__label">{t("offer.from")}</p>
        <h1 className="offer-display__company">
          {offer?.user?.company?.name}{" "}
          <span className="offer-display__tax-id">
            {offer?.user?.company?.taxId}
          </span>
        </h1>

        <div className="offer-detail-row">
          <p className="offer-detail-row__label">{t("offer.table.price")}</p>
          <div className="offer-detail-row__value">
            <OfferPriceEditor offer={offer} />
          </div>
        </div>

        <div className="offer-detail-row">
          <p className="offer-detail-row__label">{t("offer.table.quantity")}</p>
          <p className="offer-detail-row__value">
            {offer?.order?.quantity} {offer?.order?.measurementUnit}
          </p>
        </div>

        {offer?.fileURL && (
          <div className="offer-detail-row">
            <p className="offer-detail-row__label">
              {t("offer.table.technical")}
            </p>
            <div className="offer-detail-row__value">
              <FileReader fileKey={offer?.fileURL} />
            </div>
          </div>
        )}

        {offer?.expireDate && (
          <div className="offer-detail-row">
            <p className="offer-detail-row__label">
              {t("offer.table.expireDate")}
            </p>
            <p className="offer-detail-row__value">
              {formatDate(offer?.expireDate)}
            </p>
          </div>
        )}

        {offer?.comment && (
          <div className="offer-detail-row">
            <p className="offer-detail-row__label">{t("offer.comments")}</p>
            <p className="offer-detail-row__value">{offer?.comment}</p>
          </div>
        )}

        {offer?.selected && (
          <div className="offer-detail-row">
            <p className="offer-detail-row__label">
              {t("offer.table.quantitySelected")}
            </p>
            <p className="offer-detail-row__value">
              {offer?.price} {offer?.currency} / {offer?.order?.measurementUnit}
              <br /> {offer?.quantity} {offer?.order?.measurementUnit}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IsBuyerOffer;
